'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { MapPin, Phone, Mail, ChevronRight, CheckCircle, Zap, Shield, Building2, ArrowRight, Users, Globe, X, LayoutDashboard, Send, ChevronDown } from 'lucide-react';
import { createClient } from '../lib/supabase/client';

// ─── Types ────────────────────────────────────────────────────────────────────

interface MainSiteSettings {
  hero_headline: string;
  hero_subtitle: string;
  platform_logo_url: string;
  primary_color: string;
  contact_email: string;
  support_phone: string;
  social_facebook: string;
  social_instagram: string;
  social_twitter: string;
}

interface TenantVenue {
  id: string;
  name: string;
  slug: string;
  description: string;
  logo_url: string;
  featured: boolean;
  sport_types: string[];
  location: string;
}

const DEFAULTS: MainSiteSettings = {
  hero_headline: 'The Sport Arena Booking Platform',
  hero_subtitle: 'Multi-tenant SaaS powering badminton, pickleball, tennis, and more. Each arena gets their own branded portal — fully managed, instantly bookable.',
  platform_logo_url: '',
  primary_color: '#06b6d4',
  contact_email: 'hello@sellnex.net',
  support_phone: '+60 12-345 6789',
  social_facebook: '',
  social_instagram: '',
  social_twitter: '',
};

// ─── Tenant Selector Modal ────────────────────────────────────────────────────

function TenantSelectorModal({
  venues,
  onClose,
}: {
  venues: TenantVenue[];
  onClose: () => void;
}) {
  const [search, setSearch] = useState('');
  const filtered = venues.filter(
    (v) =>
      v.name.toLowerCase().includes(search.toLowerCase()) ||
      v.slug.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-[#0d1b35] border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/8">
          <div>
            <h2 className="text-white font-700 text-lg">Select Your Arena</h2>
            <p className="text-white/40 text-sm mt-0.5">Which sports arena are you booking with?</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/50 hover:text-white transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Search */}
        <div className="px-6 pt-4">
          <input
            type="text"
            placeholder="Search arenas…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full px-4 py-2.5 text-sm rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/25 focus:outline-none focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-500/40 transition-colors"
          />
        </div>

        {/* Venue list */}
        <div className="px-6 py-4 max-h-72 overflow-y-auto space-y-2">
          {filtered.length === 0 ? (
            <p className="text-white/30 text-sm text-center py-6">No arenas found.</p>
          ) : (
            filtered.map((venue) => (
              <Link
                key={venue.id}
                href={`/tenant/${venue.slug}/login`}
                className="flex items-center gap-4 p-3.5 rounded-xl bg-white/4 hover:bg-white/8 border border-white/6 hover:border-cyan-500/30 transition-all group"
              >
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-600/20 border border-white/10 flex items-center justify-center flex-shrink-0">
                  {venue.logo_url ? (
                    <img src={venue.logo_url} alt={venue.name} className="w-7 h-7 object-contain rounded" />
                  ) : (
                    <Building2 size={18} className="text-cyan-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-600 text-sm group-hover:text-cyan-300 transition-colors truncate">{venue.name}</p>
                  {venue.location && (
                    <p className="text-white/35 text-xs truncate mt-0.5">{venue.location}</p>
                  )}
                </div>
                <ChevronRight size={14} className="text-white/25 group-hover:text-cyan-400 flex-shrink-0 transition-colors" />
              </Link>
            ))
          )}
        </div>

        <div className="px-6 pb-5">
          <p className="text-white/25 text-xs text-center">
            Don&apos;t see your arena?{' '}
            <a href="mailto:hello@sellnex.net" className="text-cyan-400 hover:text-cyan-300 transition-colors">
              Contact us
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Register Arena Form ──────────────────────────────────────────────────────

function RegisterArenaForm({ contactEmail }: { contactEmail: string }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', arenaName: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In production, this would call an API endpoint
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mb-4">
          <CheckCircle size={28} className="text-emerald-400" />
        </div>
        <h3 className="text-white font-700 text-xl mb-2">Inquiry Received!</h3>
        <p className="text-white/50 text-sm max-w-xs">
          We&apos;ll reach out to <span className="text-white/70">{form.email}</span> within 24 hours to get your arena set up.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-500 text-white/40 mb-1.5">Your Name</label>
          <input
            required
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Ahmad Razif"
            className="w-full px-3 py-2.5 text-sm rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/20 focus:outline-none focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-500/40 transition-colors"
          />
        </div>
        <div>
          <label className="block text-xs font-500 text-white/40 mb-1.5">Business Email</label>
          <input
            required
            type="email"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            placeholder="admin@myarena.com"
            className="w-full px-3 py-2.5 text-sm rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/20 focus:outline-none focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-500/40 transition-colors"
          />
        </div>
        <div>
          <label className="block text-xs font-500 text-white/40 mb-1.5">Phone Number</label>
          <input
            value={form.phone}
            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            placeholder="+60 12-345 6789"
            className="w-full px-3 py-2.5 text-sm rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/20 focus:outline-none focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-500/40 transition-colors"
          />
        </div>
        <div>
          <label className="block text-xs font-500 text-white/40 mb-1.5">Arena / Business Name</label>
          <input
            required
            value={form.arenaName}
            onChange={(e) => setForm((f) => ({ ...f, arenaName: e.target.value }))}
            placeholder="DMT Smashers Badminton"
            className="w-full px-3 py-2.5 text-sm rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/20 focus:outline-none focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-500/40 transition-colors"
          />
        </div>
      </div>
      <div>
        <label className="block text-xs font-500 text-white/40 mb-1.5">Tell us about your arena (optional)</label>
        <textarea
          rows={3}
          value={form.message}
          onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
          placeholder="Number of courts, sports offered, location…"
          className="w-full px-3 py-2.5 text-sm rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/20 focus:outline-none focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-500/40 transition-colors resize-none"
        />
      </div>
      <button
        type="submit"
        className="w-full flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-700 text-sm rounded-xl shadow-lg shadow-cyan-500/20 transition-all duration-150"
      >
        <Send size={15} />
        Submit Inquiry
      </button>
    </form>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function HomePage() {
  const [settings, setSettings] = useState<MainSiteSettings>(DEFAULTS);
  const [venues, setVenues] = useState<TenantVenue[]>([]);
  const [showTenantModal, setShowTenantModal] = useState(false);
  const [loadingVenues, setLoadingVenues] = useState(true);

  useEffect(() => {
    // Detect expired/invalid password reset link errors in URL or hash
    const urlParams = new URLSearchParams(window.location.search);
    const hash = window.location.hash;
    const hashParams = new URLSearchParams(hash.replace('#', ''));

    const isExpired =
      urlParams.get('error') === 'access_denied' ||
      urlParams.get('error_code') === 'otp_expired' ||
      hashParams.get('error') === 'access_denied' ||
      hashParams.get('error_code') === 'otp_expired';

    if (isExpired) {
      window.location.replace('/auth/forgot-password?error=link_expired');
      return;
    }

    const supabase = createClient();

    // Load main site CMS settings
    supabase
      .from('site_settings')
      .select('key, value')
      .in('key', [
        'hero_headline', 'hero_subtitle', 'platform_logo_url', 'primary_color',
        'contact_email', 'support_phone', 'social_facebook', 'social_instagram', 'social_twitter'
      ])
      .then(({ data }) => {
        if (!data) return;
        const map: Record<string, string> = {};
        data.forEach((row: { key: string; value: string }) => { map[row.key] = row.value; });
        setSettings((prev) => ({ ...prev, ...map }));
      });

    // Load featured tenant venues
    supabase
      .from('businesses')
      .select('id, name, slug, description, logo_url, sport_types, city')
      .eq('is_active', true)
      .then(({ data }) => {
        if (data) {
          setVenues(
            data.map((b: {
              id: string;
              name: string;
              slug: string;
              description: string;
              logo_url: string;
              sport_types: string[];
              city: string;
            }) => ({
              id: b.id,
              name: b.name,
              slug: b.slug || b.name.toLowerCase().replace(/\s+/g, '-'),
              description: b.description || '',
              logo_url: b.logo_url || '',
              featured: true,
              sport_types: b.sport_types || [],
              location: b.city || '',
            }))
          );
        }
        setLoadingVenues(false);
      });
  }, []);

  const features = [
    {
      icon: LayoutDashboard,
      title: 'Branded Tenant Portals',
      description: 'Each arena gets a fully customizable booking portal with their own logo, colors, and domain path.',
      accent: 'from-cyan-500 to-blue-600',
      wide: true,
    },
    {
      icon: Shield,
      title: 'Secure Payments',
      description: 'Bank transfer + QR code payment with admin confirmation workflow.',
      accent: 'from-emerald-500 to-teal-600',
      wide: false,
    },
    {
      icon: Users,
      title: 'Multi-Sport Support',
      description: 'Badminton, pickleball, tennis, futsal — manage any sport type.',
      accent: 'from-blue-500 to-indigo-600',
      wide: false,
    },
    {
      icon: Zap,
      title: 'Real-Time Availability',
      description: 'Live slot management with instant booking confirmation.',
      accent: 'from-orange-500 to-amber-500',
      wide: false,
    },
    {
      icon: Globe,
      title: 'Multi-Tenant Architecture',
      description: 'Fully isolated tenant data with subdomain and path-based routing.',
      accent: 'from-purple-500 to-pink-600',
      wide: false,
    },
  ];

  return (
    <div className="min-h-screen" style={{ background: '#060d1f' }}>
      {/* Tenant Selector Modal */}
      {showTenantModal && (
        <TenantSelectorModal venues={venues} onClose={() => setShowTenantModal(false)} />
      )}

      {/* ── NAV ── */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#060d1f]/90 backdrop-blur-md border-b border-white/8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2.5 group">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center shadow-lg">
              <Zap size={18} className="text-white" fill="white" />
            </div>
            <div className="flex flex-col leading-none">
              <span className="text-white font-700 text-base tracking-tight">SellNex</span>
              <span className="text-cyan-400 text-[10px] font-500 tracking-widest uppercase">Sport Arenas</span>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm text-white/50">
            <a href="#arenas" className="hover:text-white transition-colors">Arenas</a>
            <a href="#features" className="hover:text-white transition-colors">Features</a>
            <a href="#register" className="hover:text-white transition-colors">Register Arena</a>
          </nav>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowTenantModal(true)}
              className="hidden sm:block px-4 py-2 text-sm font-600 text-white/80 hover:text-white border border-white/20 hover:border-white/40 rounded-xl transition-all duration-150"
            >
              Sign In
            </button>
            <Link
              href="/admin/super-login"
              className="px-4 py-2 text-sm font-600 text-amber-300 hover:text-amber-200 border border-amber-500/30 hover:border-amber-500/50 rounded-xl transition-all duration-150"
            >
              Super Admin
            </Link>
          </div>
        </div>
      </header>

      <main>
        {/* ── HERO ── */}
        <section
          className="relative min-h-screen flex items-center pt-16 overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #060d1f 0%, #0a1628 40%, #0d1f3c 70%, #091520 100%)' }}
        >
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div
              className="absolute inset-0 opacity-[0.03]"
              style={{
                backgroundImage: `linear-gradient(to right, #00d4ff 1px, transparent 1px), linear-gradient(to bottom, #00d4ff 1px, transparent 1px)`,
                backgroundSize: '80px 80px',
              }}
            />
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
            <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-blue-600/10 rounded-full blur-3xl" />
            <div className="absolute top-1/2 right-1/3 w-64 h-64 bg-purple-500/8 rounded-full blur-3xl" />
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32 w-full">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-cyan-500/15 border border-cyan-500/30 rounded-full mb-8">
                <span className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse" />
                <span className="text-cyan-300 text-xs font-600 tracking-wide">Multi-Tenant SaaS Platform</span>
              </div>

              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-700 text-white leading-[1.05] tracking-tight mb-6">
                {settings.hero_headline.split(' ').slice(0, -2).join(' ')}{' '}
                <span
                  style={{
                    background: 'linear-gradient(90deg, #22d3ee 0%, #06b6d4 40%, #84cc16 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  {settings.hero_headline.split(' ').slice(-2).join(' ')}
                </span>
              </h1>

              <p className="text-lg text-white/55 leading-relaxed max-w-2xl mb-10">
                {settings.hero_subtitle}
              </p>

              <div className="flex flex-wrap gap-4">
                <a
                  href="#arenas"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-700 rounded-xl shadow-xl shadow-cyan-500/25 transition-all duration-200"
                >
                  Browse Arenas
                  <ChevronRight size={18} />
                </a>
                <a
                  href="#register"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-white/8 hover:bg-white/14 text-white font-600 rounded-xl border border-white/15 hover:border-white/30 transition-all duration-200"
                >
                  Register Your Arena
                </a>
              </div>

              <div className="flex flex-wrap gap-6 mt-10">
                {[
                  { value: 'Multi-Sport', label: 'Badminton, Pickleball, Tennis & more' },
                  { value: 'White-Label', label: 'Fully branded per tenant' },
                  { value: 'Real-Time', label: 'Live availability & bookings' },
                ].map((stat) => (
                  <div key={stat.label} className="flex items-center gap-2">
                    <CheckCircle size={14} className="text-cyan-400/70 flex-shrink-0" />
                    <span className="text-white/50 text-sm">
                      <span className="text-white/80 font-600">{stat.value}</span> — {stat.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Scroll indicator */}
            <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/20">
              <span className="text-xs tracking-widest uppercase">Scroll</span>
              <ChevronDown size={16} className="animate-bounce" />
            </div>
          </div>
        </section>

        {/* ── ARENA DIRECTORY ── */}
        <section id="arenas" className="py-20 lg:py-28 bg-[#0a1628]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12">
              <div>
                <span className="text-cyan-400 text-xs font-700 uppercase tracking-widest">Arena Directory</span>
                <h2 className="text-3xl lg:text-4xl font-700 text-white mt-2 leading-tight">
                  Active Sports Venues
                </h2>
                <p className="text-white/45 mt-2 text-base">
                  Select your arena to book courts and manage your sessions.
                </p>
              </div>
              <button
                onClick={() => setShowTenantModal(true)}
                className="flex-shrink-0 inline-flex items-center gap-2 px-5 py-2.5 bg-white/6 hover:bg-white/10 text-white/70 hover:text-white text-sm font-600 rounded-xl border border-white/10 hover:border-white/20 transition-all"
              >
                <Building2 size={15} />
                View All Arenas
              </button>
            </div>

            {loadingVenues ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-white/4 border border-white/6 rounded-2xl p-6 animate-pulse">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 rounded-xl bg-white/8" />
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-white/8 rounded w-3/4" />
                        <div className="h-3 bg-white/5 rounded w-1/2" />
                      </div>
                    </div>
                    <div className="h-3 bg-white/5 rounded w-full mb-2" />
                    <div className="h-8 bg-white/5 rounded-lg mt-4" />
                  </div>
                ))}
              </div>
            ) : venues.length === 0 ? (
              <div className="text-center py-20 bg-white/3 border border-white/6 rounded-2xl">
                <Building2 size={40} className="text-white/15 mx-auto mb-4" />
                <p className="text-white/30 text-base font-500">No arenas registered yet.</p>
                <p className="text-white/20 text-sm mt-1">Be the first to register your arena below.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {venues.map((venue) => (
                  <div
                    key={venue.id}
                    className="group bg-white/4 hover:bg-white/7 border border-white/6 hover:border-cyan-500/25 rounded-2xl p-6 transition-all duration-200"
                  >
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/15 to-blue-600/15 border border-white/10 flex items-center justify-center flex-shrink-0">
                        {venue.logo_url ? (
                          <img src={venue.logo_url} alt={`${venue.name} logo`} className="w-8 h-8 object-contain rounded" />
                        ) : (
                          <Building2 size={20} className="text-cyan-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-white font-700 text-base group-hover:text-cyan-300 transition-colors truncate">
                          {venue.name}
                        </h3>
                        {venue.location && (
                          <p className="text-white/35 text-xs mt-0.5 flex items-center gap-1">
                            <MapPin size={10} />
                            {venue.location}
                          </p>
                        )}
                      </div>
                      <span className="flex-shrink-0 w-2 h-2 bg-emerald-400 rounded-full mt-1.5" title="Active" />
                    </div>

                    {venue.sport_types && venue.sport_types.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-4">
                        {venue.sport_types.slice(0, 3).map((sport) => (
                          <span key={sport} className="px-2 py-0.5 bg-white/6 border border-white/8 rounded-full text-white/50 text-xs">
                            {sport}
                          </span>
                        ))}
                      </div>
                    )}

                    {venue.description && (
                      <p className="text-white/35 text-sm line-clamp-2 mb-4">{venue.description}</p>
                    )}

                    <Link
                      href={`/tenant/${venue.slug}`}
                      className="flex items-center justify-between w-full px-4 py-2.5 bg-white/6 hover:bg-cyan-500/15 border border-white/8 hover:border-cyan-500/30 rounded-xl text-white/70 hover:text-cyan-300 text-sm font-600 transition-all group/btn"
                    >
                      <span>Visit Portal</span>
                      <ArrowRight size={14} className="group-hover/btn:translate-x-0.5 transition-transform" />
                    </Link>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* ── PLATFORM FEATURES ── */}
        <section id="features" className="py-20 lg:py-28" style={{ background: '#060d1f' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-12 max-w-2xl">
              <span className="text-cyan-400 text-xs font-700 uppercase tracking-widest">Platform Features</span>
              <h2 className="text-3xl lg:text-4xl font-700 text-white mt-2 leading-tight">
                Built for Arena Operators
              </h2>
              <p className="text-white/45 mt-3 text-base leading-relaxed">
                Everything you need to run a modern sports booking business — from branding to payments.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {features.map((feature, i) => {
                const FeatureIcon = feature.icon;
                return (
                  <div
                    key={feature.title}
                    className={`relative bg-white/4 hover:bg-white/7 border border-white/8 hover:border-white/16 rounded-2xl p-6 transition-all duration-200 group ${
                      feature.wide ? 'sm:col-span-2 lg:col-span-2' : ''
                    }`}
                  >
                    <div
                      className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feature.accent} flex items-center justify-center shadow-lg mb-5 group-hover:scale-105 transition-transform duration-200`}
                    >
                      <FeatureIcon size={22} className="text-white" />
                    </div>
                    <h3 className="text-white font-700 text-base mb-2">{feature.title}</h3>
                    <p className="text-white/50 text-sm leading-relaxed">{feature.description}</p>
                    {feature.wide && (
                      <a
                        href="#register"
                        className="inline-flex items-center gap-1.5 mt-5 text-cyan-400 text-sm font-600 hover:text-cyan-300 transition-colors"
                      >
                        Register your arena <ArrowRight size={14} />
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── REGISTER YOUR ARENA ── */}
        <section id="register" className="py-20 lg:py-28 bg-[#0a1628]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-[1fr_520px] gap-12 lg:gap-16 items-start">
              {/* Left: Copy */}
              <div className="space-y-6">
                <div>
                  <span className="text-cyan-400 text-xs font-700 uppercase tracking-widest">For Arena Owners</span>
                  <h2 className="text-3xl lg:text-4xl font-700 text-white mt-2 leading-tight">
                    Register Your Arena
                  </h2>
                  <p className="text-white/45 mt-3 text-base leading-relaxed">
                    Join the platform and get your own branded booking portal. Set up in minutes, start accepting bookings today.
                  </p>
                </div>

                <div className="space-y-4">
                  {[
                    { icon: Zap, title: 'Quick Setup', desc: 'Your portal is live within 24 hours of approval.' },
                    { icon: Globe, title: 'Custom Branding', desc: 'Your logo, colors, and domain path — fully yours.' },
                    { icon: Shield, title: 'Secure & Reliable', desc: 'Bank-grade security with 99.9% uptime guarantee.' },
                    { icon: Users, title: 'Customer Management', desc: 'Built-in user accounts, booking history, and notifications.' },
                  ].map((item) => {
                    const ItemIcon = item.icon;
                    return (
                      <div key={item.title} className="flex items-start gap-4">
                        <div className="w-9 h-9 rounded-xl bg-cyan-500/15 border border-cyan-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <ItemIcon size={16} className="text-cyan-400" />
                        </div>
                        <div>
                          <p className="text-white font-600 text-sm">{item.title}</p>
                          <p className="text-white/40 text-sm mt-0.5">{item.desc}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="flex items-center gap-3 pt-2">
                  <div className="flex -space-x-2">
                    {['DM', 'AR', 'KL', 'SN'].map((init) => (
                      <div
                        key={init}
                        className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500/30 to-blue-600/30 border-2 border-[#0a1628] flex items-center justify-center text-white text-xs font-600"
                      >
                        {init}
                      </div>
                    ))}
                  </div>
                  <p className="text-white/35 text-sm">Join arena operators already on the platform</p>
                </div>
              </div>

              {/* Right: Form */}
              <div className="bg-white/4 border border-white/8 rounded-2xl p-8">
                <div className="mb-6">
                  <h3 className="text-white font-700 text-xl">Business Inquiry</h3>
                  <p className="text-white/40 text-sm mt-1">Fill in your details and we&apos;ll get you set up.</p>
                </div>
                <RegisterArenaForm contactEmail={settings.contact_email} />
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* ── FOOTER ── */}
      <footer className="bg-[#060d1f] border-t border-white/8 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
            <Link href="/" className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center">
                <Zap size={15} className="text-white" fill="white" />
              </div>
              <div className="flex flex-col leading-none">
                <span className="text-white/70 text-sm font-700">SellNex Sport Arenas</span>
                <span className="text-white/25 text-[10px]">Multi-Tenant Booking Platform</span>
              </div>
            </Link>

            <div className="flex items-center gap-5 text-xs text-white/30">
              {settings.support_phone && (
                <a href={`tel:${settings.support_phone}`} className="flex items-center gap-1.5 hover:text-white/60 transition-colors">
                  <Phone size={12} />
                  {settings.support_phone}
                </a>
              )}
              {settings.contact_email && (
                <a href={`mailto:${settings.contact_email}`} className="flex items-center gap-1.5 hover:text-white/60 transition-colors">
                  <Mail size={12} />
                  {settings.contact_email}
                </a>
              )}
            </div>

            <div className="flex items-center gap-4 text-xs text-white/20">
              <Link href="/admin/super-login" className="hover:text-white/40 transition-colors">Admin Login</Link>
              <span>·</span>
              <span>© 2026 SellNex</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}